/* The logo keeps its native image and link. Lighting uses alpha masks, and an
   optional float adds slow depth drift. Only visible, opted-in motion runs a
   frame loop; sparkles use CSS and pause with the same visibility contract. */
(() => {
  'use strict';
  if (customElements.get('logo-foil')) return;
  const clamp = (value, min = -1, max = 1) => Math.max(min, Math.min(max, value));
  const zero = () => ({ gx: 0, gy: 0, lx: 0, ly: 0 });

  class LogoFoil extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.link = this.closest('a');
      if (!this.link) return;
      this.screen = this.closest('[data-og-screen]') || this.link;
      this.face = this.querySelector('.logo-foil__face');
      this.events = new AbortController();
      this.generation = (this.generation || 0) + 1;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(any-hover: hover) and (any-pointer: fine)');
      this.current = zero();
      this.target = zero();
      this.properties = new Map();
      this.elapsed = 0;
      this.lastTime = 0;
      this.hovered = false;
      this.focused = false;
      this.visible = !('IntersectionObserver' in window);
      this.tick = this.tick.bind(this);
      const options = { signal: this.events.signal };
      const style = getComputedStyle(this);
      const number = (name, fallback) => {
        const value = parseFloat(style.getPropertyValue('--logo-foil-' + name));
        return Number.isFinite(value) ? value : fallback;
      };
      this.tilt = this.hasAttribute('data-tilt') ? number('tilt', 4) : 0;
      this.depth = this.hasAttribute('data-depth') ? number('depth', 2) / 100 : 0;
      this.float = this.hasAttribute('data-float') ? number('float', 1) / 100 : 0;
      this.loopSweep = this.dataset.sweep === 'ambient';

      this.screen.addEventListener('pointermove', (event) => this.move(event), options);
      this.screen.addEventListener('pointerleave', () => {
        this.target.gx = this.target.gy = 0;
        this.leave();
      }, options);
      this.link.addEventListener('pointerleave', () => this.leave(), options);
      this.link.addEventListener('pointercancel', () => this.leave(), options);
      this.link.addEventListener('dragstart', (event) => event.preventDefault(), options);
      this.link.addEventListener('focus', () => {
        if (!this.link.matches(':focus-visible')) return;
        this.focused = true;
        this.target.lx = 0.35;
        this.target.ly = -0.15;
        this.activate();
      }, options);
      this.link.addEventListener('blur', () => {
        this.focused = false;
        this.leave();
      }, options);
      this.reduced.addEventListener('change', () => this.sync(), options);
      this.pointer.addEventListener('change', () => {
        this.hovered = false;
        this.current = zero();
        this.target = zero();
        this.activate();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.link);
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.sync();
        });
        this.intersectionObserver.observe(this);
      }
      this.measure();
      this.makeSparkles(this.generation);
      this.setAttribute('data-ready', '');
      this.sync();
    }

    measure() {
      this.size = Math.max(1, this.link.offsetWidth, this.link.offsetHeight);
      this.write('rest-depth', this.size * this.depth * 0.65, 'px');
      this.write('spark-size', clamp(this.size * 0.045, 3, 7), 'px');
      this.sync();
    }

    move(event) {
      if (event.pointerType === 'touch' || !this.pointer.matches || this.reduced.matches) return;
      const onLogo = this.link.contains(event.target);
      if (!this.float && !onLogo) return;
      const rect = this.link.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      this.hovered = onLogo;
      if (this.float) {
        this.target.gx = clamp((event.clientX - rect.left - rect.width / 2) / (innerWidth / 2));
        this.target.gy = clamp((event.clientY - rect.top - rect.height / 2) / (innerHeight / 2));
      }
      if (onLogo) {
        this.target.lx = clamp((event.clientX - rect.left) / rect.width * 2 - 1);
        this.target.ly = clamp((event.clientY - rect.top) / rect.height * 2 - 1);
      }
      this.activate();
    }

    leave() {
      this.hovered = false;
      this.target.lx = this.current.lx = this.focused ? 0.35 : 0;
      this.target.ly = this.current.ly = this.focused ? -0.15 : 0;
      this.activate();
      this.render();
    }

    activate() {
      this.toggleAttribute('data-active', this.hovered || this.focused);
      this.sync();
    }

    canRun() {
      return Boolean(this.events && this.visible && !document.hidden && !this.reduced.matches);
    }

    unsettled() {
      return Object.keys(this.target).some((key) => Math.abs(this.target[key] - this.current[key]) > 0.001);
    }

    sync() {
      const running = this.canRun();
      this.toggleAttribute('data-running', running);
      if (!running) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.lastTime = 0;
        this.current = zero();
        this.target = zero();
        this.render();
        return;
      }
      if (!this.frame && (this.float || this.loopSweep || this.unsettled())) {
        this.frame = requestAnimationFrame(this.tick);
      }
    }

    tick(now) {
      this.frame = 0;
      if (!this.canRun()) return;
      const dt = this.lastTime ? clamp((now - this.lastTime) / 1000, 0, 0.05) : 1 / 60;
      this.lastTime = now;
      this.elapsed += dt;
      const ease = 1 - Math.exp(-dt * (this.float ? 5 : 14));
      for (const key of Object.keys(this.target)) {
        this.current[key] += (this.target[key] - this.current[key]) * ease;
        if (Math.abs(this.target[key] - this.current[key]) < 0.001) this.current[key] = this.target[key];
      }
      this.render();
      if (this.float || this.loopSweep || this.unsettled()) {
        this.frame = requestAnimationFrame(this.tick);
      } else {
        this.lastTime = 0;
      }
    }

    write(name, value, unit = '') {
      const result = value.toFixed(3) + unit;
      if (this.properties.get(name) === result) return;
      this.properties.set(name, result);
      this.style.setProperty('--logo-foil-' + name, result);
    }

    render() {
      if (!this.properties) return;
      const running = this.canRun();
      const t = this.elapsed;
      const drift = running ? this.float * this.size : 0;
      const { gx, gy, lx, ly } = running ? this.current : zero();
      const floatX = Math.sin(t * 0.39) * drift * 0.6 + gx * drift;
      const floatY = Math.sin(t * 0.56) * drift + gy * drift;
      const floatZ = Math.sin(t * 0.32) * drift * 3;
      this.write('tx', floatX, 'px');
      this.write('ty', floatY, 'px');
      this.write('tz', floatZ, 'px');
      this.write('rx', -ly * this.tilt * 0.67 + (drift ? Math.sin(t * 0.31) * 0.6 - gy : 0), 'deg');
      this.write('ry', lx * this.tilt + (drift ? Math.sin(t * 0.4) * 0.8 + gx : 0), 'deg');
      const depth = this.depth * this.size;
      const px = (lx * 0.7 + gx * 0.5) * depth + (drift ? Math.sin(t * 0.39) * depth * 0.2 : 0);
      const py = (ly * 0.7 + gy * 0.5) * depth + (drift ? Math.sin(t * 0.56) * depth * 0.2 : 0);
      this.write('px', px, 'px');
      this.write('py', py, 'px');
      const viewX = lx + gx * 0.4 + (drift ? Math.sin(t * 0.4) * 0.22 : 0);
      const viewY = ly + gy * 0.4;
      this.write('x', 50 + clamp(viewX) * 32, '%');
      this.write('y', 50 + clamp(viewY) * 20, '%');
      if (this.loopSweep && !this.hovered && !this.focused && running) {
        // One short, eased sweep in an otherwise quiet ten-second cycle.
        const phase = (t % 10) / 2.4;
        const progress = clamp(phase, 0, 1);
        const eased = progress * progress * (3 - 2 * progress);
        this.write('sweep-x', -5 + eased * 110, '%');
        this.write('sweep-level', phase < 1 ? Math.sin(progress * Math.PI) : 0);
      } else {
        this.write('sweep-x', 50 + clamp(viewX) * 42, '%');
        this.write('sweep-level', 1);
      }
    }

    async makeSparkles(generation) {
      const container = this.querySelector('.logo-foil__sparkles');
      if (!container) return;
      const image = this.querySelector('[data-logo-source]');
      try {
        if (image) await image.decode();
        else if (document.fonts) await document.fonts.ready;
        if (!this.events || generation !== this.generation) return;
        const width = image ? image.naturalWidth : this.link.offsetWidth;
        const height = image ? image.naturalHeight : this.link.offsetHeight;
        if (!width || !height) return;
        const scale = 160 / Math.max(width, height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        if (image) context.drawImage(image, 0, 0, canvas.width, canvas.height);
        else this.drawTextMask(context, scale, width);
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
        const w = canvas.width, h = canvas.height;
        const alpha = (x, y) => x < 0 || y < 0 || x >= w || y >= h ? 0 : data[(y * w + x) * 4 + 3];
        const points = [];
        for (let y = 0; y < h; y += 1) {
          for (let x = 0; x < w; x += 1) {
            if (alpha(x, y) > 100 && [alpha(x - 1, y), alpha(x + 1, y), alpha(x, y - 1), alpha(x, y + 1)].some(a => a < 80)) {
              points.push({ x: (x + 0.5) / w, y: (y + 0.5) / h });
            }
          }
        }
        const chosen = [];
        for (const [x, y] of [[0.2, 0.2], [0.82, 0.36], [0.7, 0.85], [0.24, 0.7]]) {
          const available = points.filter(p => chosen.every(c => Math.hypot(p.x - c.x, p.y - c.y) > 0.18));
          available.sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y));
          if (available[0]) chosen.push(available[0]);
        }
        const fragment = document.createDocumentFragment();
        chosen.forEach((point, index) => {
          const spark = document.createElement('span');
          spark.className = 'logo-foil__spark';
          spark.style.left = (point.x * 100).toFixed(2) + '%';
          spark.style.top = (point.y * 100).toFixed(2) + '%';
          spark.style.setProperty('--spark-delay', (-index * 0.73).toFixed(2) + 's');
          spark.style.setProperty('--spark-duration', (2.8 + index * 0.41).toFixed(2) + 's');
          fragment.append(spark);
        });
        container.replaceChildren(fragment);
      } catch (_) {
        // Unreadable/cross-origin image pixels must never break the logo.
        // The image and all CSS material effects remain usable.
      }
    }

    drawTextMask(context, scale, width) {
      const style = getComputedStyle(this.face);
      let text = Array.from(this.face.childNodes).filter(n => n.nodeType === 3).map(n => n.textContent).join('').trim();
      if (style.textTransform === 'uppercase') text = text.toUpperCase();
      else if (style.textTransform === 'lowercase') text = text.toLowerCase();
      else if (style.textTransform === 'capitalize') text = text.replace(/(^|\s)\S/g, c => c.toUpperCase());
      context.scale(scale, scale);
      context.font = style.fontStyle + ' ' + style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
      context.fillStyle = '#ffffff';
      context.textBaseline = 'middle';
      context.textAlign = ['left', 'center', 'right'].includes(style.textAlign) ? style.textAlign : 'left';
      const x = context.textAlign === 'center' ? width / 2 : context.textAlign === 'right' ? width : 0;
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
      const lines = [];
      let line = '';
      for (const word of text.split(/\s+/)) {
        const next = line ? line + ' ' + word : word;
        if (line && context.measureText(next).width > width) { lines.push(line); line = word; }
        else line = next;
      }
      if (line) lines.push(line);
      lines.forEach((line, i) => context.fillText(line, x, (i + 0.5) * lineHeight));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.generation += 1;
      this.removeAttribute('data-ready');
      this.removeAttribute('data-running');
      this.removeAttribute('data-active');
      this.properties?.forEach((_, key) => this.style.removeProperty('--logo-foil-' + key));
      this.properties?.clear();
    }
  }

  customElements.define('logo-foil', LogoFoil);
})();
